/**
 * Email-body template renderer.
 *
 * The template is content-driven (lives in landing-email-template.json /
 * `landing/email-template` content surface). We render with a tiny
 * `{{ path.to.field }}` substitution — no Mustache dep, no Handlebars.
 *
 * Behavior preserved from App.tsx lines 2094-2127 (the literal ASCII-art
 * body). Test verifies the rendered output matches the legacy hardcoded
 * string byte-for-byte for a known input.
 */

import type { CustomerInfo, Cart, SubmissionData } from '../types';
import { formatMoney, stripBoxSuffix } from '@brandbox/content-schema';
import type { SubmissionOrderStep } from '@brandbox/content-schema';

export interface EmailTemplateData {
  subjectTemplate: string;
  bodyHtmlTemplate: string;
  fromAddress?: string;
}

export interface EmailContext {
  customer: {
    company: string;
    fullName: string;
    email: string;
    phone: string;
  };
  box: {
    themeId: string;
  };
  qty: number;
  packaging: { summary: string };
  merch: { summary: string };
  finalTouch: { summary: string };
  deliveryDate: string;
  storage: string;
  shipment: string;
  pricing: { formattedTotal: string };
  notes: string;
}

/**
 * Format the total for the customer email.
 *
 * Was App.tsx line 2092 verbatim:
 * `${currency} ${total.toLocaleString('nl-NL', { minimumFractionDigits: 2 })}`
 *
 * Chapter 10 (P3a) delegates to the shared `formatMoney`. That is not only
 * de-duplication — the old call had a `minimumFractionDigits` with no matching
 * maximum, and `toLocaleString` defaults to a maximum of THREE. A VAT-derived
 * total therefore reached customers as "€ 324,945". Money is two decimals.
 */
export function formatPriceForEmail(total: number, currencySymbol: string): string {
  return formatMoney(total, currencySymbol);
}

/**
 * Tiny template engine. Replaces `{{ path.to.value }}` (whitespace optional)
 * with the matching string from `ctx`. Missing paths render as the empty
 * string — callers fill defaults upstream.
 */
export function renderTemplate(template: string, ctx: Record<string, unknown>): string {
  return template.replace(/{{\s*([^}\s]+)\s*}}/g, (_match, path: string) => {
    const segs = path.split('.');
    let cur: unknown = ctx;
    for (const seg of segs) {
      if (cur && typeof cur === 'object' && seg in (cur as Record<string, unknown>)) {
        cur = (cur as Record<string, unknown>)[seg];
      } else {
        return '';
      }
    }
    return cur == null ? '' : String(cur);
  });
}

/**
 * Build the per-flow EmailContext from cart + customer + selections.
 * Mirrors App.tsx lines 2070-2092.
 */
export function buildEmailContext(args: {
  customerInfo: CustomerInfo;
  cart: Cart;
  packagingLabel: string;
  packagingOther: string;
  merchLabels: string[];
  merchOther: string;
  finalTouchLabels: string[];
  finalTouchOther: string;
  deliveryDate: string;
  storage: string;
  shipment: string;
  notes: string;
}): EmailContext {
  const packagingSummary = args.packagingOther
    ? `${args.packagingLabel} (Custom: ${args.packagingOther})`
    : args.packagingLabel;

  const merchSummary =
    [
      ...args.merchLabels,
      ...(args.merchOther ? [`Other: ${args.merchOther}`] : []),
    ].join(', ') || 'None selected';

  const finalTouchSummary =
    [
      ...args.finalTouchLabels,
      ...(args.finalTouchOther ? [`Other: ${args.finalTouchOther}`] : []),
    ].join(', ') || 'None selected';

  return {
    customer: {
      company: String(args.customerInfo.company || 'Private'),
      fullName: String(args.customerInfo.fullName || ''),
      email: String(args.customerInfo.email || ''),
      phone: String(args.customerInfo.phone || 'N/A'),
    },
    // The body header (`🎁 ORDER DETAILS: {{ box.themeId }} BOX`) supplies the
    // word BOX itself — every authored template does, and the admin token
    // picker documents this token's sample as the bare "SPRING". So the token
    // gets the BARE name: the admin-chosen display name with a trailing " BOX"
    // stripped, falling back to the uppercased slug for legacy/empty orders.
    //
    // Chapter 10 (F22): feeding the full display name here is what made the
    // LIVE email read "🎁 ORDER DETAILS: SPRING BOX BOX".
    box: {
      themeId: stripBoxSuffix(
        String(
          args.cart.box.boxName && args.cart.box.boxName.trim().length > 0
            ? args.cart.box.boxName
            : args.cart.box.themeId,
        ).toUpperCase(),
      ),
    },
    qty: args.cart.qty,
    packaging: { summary: packagingSummary },
    merch: { summary: merchSummary },
    finalTouch: { summary: finalTouchSummary },
    deliveryDate: args.deliveryDate || 'To be decided',
    storage: args.storage,
    shipment: args.shipment,
    pricing: {
      formattedTotal: formatPriceForEmail(args.cart.pricing.total, args.cart.pricing.currency),
    },
    notes: args.notes || 'No additional notes provided.',
  };
}

/**
 * Render the body + subject. Returns the strings the legacy code put in
 * SubmissionData.summary.emailContent.
 */
export function renderEmail(template: EmailTemplateData, ctx: EmailContext): {
  subject: string;
  body: string;
} {
  return {
    subject: renderTemplate(template.subjectTemplate, ctx as unknown as Record<string, unknown>),
    body: renderTemplate(template.bodyHtmlTemplate, ctx as unknown as Record<string, unknown>),
  };
}

/**
 * The `design` block of a submission — Chapter 10 (F15).
 *
 * Three shapes, one function, because they must not drift:
 *
 *   - no logo → `{ logoProvided: false }`, byte-identical to legacy.
 *   - logos attached → `logos[]` with an entry per file, PLUS the first
 *     entry's filename/size/pointer mirrored onto the legacy fields. The mirror
 *     is what keeps every existing reader (the admin detail view, both exports,
 *     both email renderers) working with no migration.
 *   - logos attached but not stored (upload failed, customer chose to send
 *     anyway) → the same, minus every `blob`. The filenames are what sales
 *     chases the customer for.
 */
function buildDesign(
  logos: readonly File[],
  blobs: readonly {
    container: string;
    path: string;
    contentType: string;
    sizeBytes: number;
  }[],
): SubmissionData['design'] {
  if (logos.length === 0) return { logoProvided: false };
  const entries = logos.map((file, i) => ({
    filename: file.name,
    sizeKB: Math.round(file.size / 1024),
    // All-or-nothing upload: either every file has a pointer or none does.
    ...(blobs[i] ? { blob: blobs[i] } : {}),
  }));
  const first = entries[0]!;
  return {
    logoProvided: true,
    filename: first.filename,
    sizeKB: first.sizeKB,
    ...(first.blob ? { logoBlob: first.blob } : {}),
    logos: entries,
  };
}

/**
 * Compatibility shim: build the SubmissionData payload exactly as App.tsx
 * lines 2129-2199 did. Receives the rendered email body so the caller
 * decides which template (content-driven or legacy fallback) to use.
 */
export function buildSubmission(args: {
  flowId: string;
  cart: Cart;
  customerInfo: CustomerInfo;
  packagingLabel: string;
  merchLabels: { id: string; label: string }[];
  finalTouchLabels: { id: string; label: string }[];
  deliveryDate: string;
  storage: string;
  shipment: string;
  notes: string;
  /**
   * Chapter 10 (F15) — every logo the customer attached, in the order they
   * picked them. Empty for an order with no logo, which keeps the design block
   * byte-identical to legacy (`{ logoProvided: false }`).
   */
  logos: readonly File[];
  /**
   * M4 (customer-logo pipeline) — pointers to the stored logo blobs, present
   * ONLY when the bytes were uploaded to `POST /api/send/logo`. The upload is
   * all-or-nothing, so this is either as long as `logos` or empty; when empty
   * (no logo, or the upload failed and the customer chose to proceed) no blob
   * pointer appears anywhere in the body and only the filenames + sizes flow,
   * exactly as before the blob pipeline existed.
   */
  logoBlobs?: readonly {
    container: string;
    path: string;
    contentType: string;
    sizeBytes: number;
  }[];
  agreedToNewsletter: boolean;
  emailBody: string;
  formattedPrice: string;
  /**
   * Phase N2b — the generic per-step selections array. Optional so existing
   * callers/tests that don't pass it still build the legacy shape unchanged
   * (Spring/Office byte-identical). When present it's attached to
   * `order.steps[]` server-side via the /api/send adapter.
   */
  steps?: SubmissionOrderStep[];
}): SubmissionData {
  const { cart, customerInfo } = args;
  const packagingSummary = cart.box.packagingOther
    ? `${args.packagingLabel} (Custom: ${cart.box.packagingOther})`
    : args.packagingLabel;
  const merchSummary = [
    ...args.merchLabels.map((m) => m.label),
    ...(cart.box.merchOther ? [`Other: ${cart.box.merchOther}`] : []),
  ].join(', ');
  const finalTouchSummary = [
    ...args.finalTouchLabels.map((m) => m.label),
    ...(cart.box.finalTouchOther ? [`Other: ${cart.box.finalTouchOther}`] : []),
  ].join(', ');

  return {
    flowId: args.flowId,
    summary: {
      customer: `${customerInfo.company || 'Private'} (${customerInfo.fullName || 'N/A'})`,
      email: customerInfo.email || '',
      phone: String(customerInfo.phone || ''),
      quantity: String(cart.qty),
      // Human-facing box name. Prefer the admin-chosen display name
      // (`cart.box.boxName`, e.g. "SPRING BOX"); fall back to the uppercased
      // slug for legacy/empty cases so this is never blank.
      theme:
        cart.box.boxName && cart.box.boxName.trim().length > 0
          ? cart.box.boxName
          : String(cart.box.themeId).toUpperCase(),
      packaging: packagingSummary,
      merch: merchSummary,
      finalTouch: finalTouchSummary,
      deliveryDate: args.deliveryDate || 'Not specified',
      logistics: `Storage: ${args.storage}, Shipment: ${args.shipment}`,
      totalPrice: args.formattedPrice,
      emailContent: args.emailBody,
    },
    items: {
      packaging: {
        id: cart.box.packagingId,
        label: args.packagingLabel,
        customText: cart.box.packagingOther,
      },
      merch: [
        ...args.merchLabels.map(({ id, label }) => ({ id, label })),
        ...(cart.box.merchOther
          ? [{ id: 'other', label: 'Other Merch', customText: cart.box.merchOther }]
          : []),
      ],
      finalTouch: [
        ...args.finalTouchLabels.map(({ id, label }) => ({ id, label })),
        ...(cart.box.finalTouchOther
          ? [{ id: 'other', label: 'Other Final Touch', customText: cart.box.finalTouchOther }]
          : []),
      ],
    },
    box: {
      themeId: cart.box.themeId,
      // Persist the admin-chosen display name onto the order so admin/orders
      // shows the real name, not the slug. Omitted when empty (keeps legacy
      // order bodies byte-identical for callers without a boxName).
      ...(cart.box.boxName && cart.box.boxName.trim().length > 0
        ? { boxName: cart.box.boxName }
        : {}),
      packagingId: cart.box.packagingId,
      packagingOther: cart.box.packagingOther,
      merchIds: cart.box.merchIds,
      merchOther: cart.box.merchOther,
      finalTouchIds: cart.box.finalTouchIds,
      finalTouchOther: cart.box.finalTouchOther,
    },
    quantity: cart.qty,
    pricing: {
      unit: cart.pricing.unit,
      discountPct: 0,
      total: cart.pricing.total,
      currency: cart.pricing.currency || '€',
    },
    storage: args.storage,
    shipment: args.shipment,
    design: buildDesign(args.logos, args.logoBlobs ?? []),
    deliveryDate: args.deliveryDate || null,
    notes: args.notes || null,
    // Phase N2b — generic per-step selections (every option step). The
    // /api/send adapter lifts this into `order.steps[]`. Omitted entirely
    // when absent so the legacy body is byte-identical for callers that
    // don't pass it.
    ...(args.steps && args.steps.length > 0 ? { steps: args.steps } : {}),
    contact: {
      company: customerInfo.company || '',
      fullName: customerInfo.fullName || '',
      email: customerInfo.email || '',
      phone: customerInfo.phone || '',
    },
    consent: {
      termsAccepted: true,
      newsletter: args.agreedToNewsletter,
    },
    meta: {
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
    },
  };
}

/**
 * Legacy in-code email body (kept as the fallback if content API + JSON
 * are both unreachable). Preserves App.tsx lines 2094-2127 verbatim.
 */
export const LEGACY_EMAIL_TEMPLATE: EmailTemplateData = {
  subjectTemplate: 'BrandBox proposal request — {{ customer.company }}',
  bodyHtmlTemplate: [
    `📦 NEW BRANDBOX PROPOSAL REQUEST`,
    `==================================================`,
    ``,
    `👤 CUSTOMER INFORMATION`,
    `--------------------------------------------------`,
    `🏢 Company:      {{ customer.company }}`,
    `👤 Contact:      {{ customer.fullName }}`,
    `📧 Email:        {{ customer.email }}`,
    `📞 Phone:        {{ customer.phone }}`,
    ``,
    `🎁 ORDER DETAILS: {{ box.themeId }} BOX`,
    `--------------------------------------------------`,
    `🔢 Quantity:     {{ qty }} boxes`,
    `📦 Packaging:    {{ packaging.summary }}`,
    `👕 Merchandise:  {{ merch.summary }}`,
    `✨ Final Touch:  {{ finalTouch.summary }}`,
    ``,
    `🚚 LOGISTICS & TIMING`,
    `--------------------------------------------------`,
    `📅 Delivery:     {{ deliveryDate }}`,
    `🏠 Storage:      {{ storage }}`,
    `🚢 Shipment:     {{ shipment }}`,
    ``,
    `💰 PRICING SUMMARY`,
    `--------------------------------------------------`,
    `💵 Total Est:    {{ pricing.formattedTotal }}`,
    `==================================================`,
    ``,
    `📝 ADDITIONAL NOTES:`,
    `{{ notes }}`,
    ``,
    `Generated via BrandBox Studio Configurator`,
  ].join('\n'),
};
